<script>
  import { createEventDispatcher } from 'svelte';
  
  const dispatch = createEventDispatcher();
  
  export let items = [];
  export let placeholder = "New item";
  export let minItems = 0;
  export let allowEmpty = true;
  export let itemType = 'text'; // 'text', 'number', 'textarea'
  
  // Internal state for working with items
  let workingItems = [];
  
  // Sync working items with prop changes
  $: workingItems = Array.isArray(items) ? [...items] : [];
  
  function addItem() {    
    const newItems = [...workingItems, ''];
    workingItems = newItems;
    dispatch('update', newItems);
  }
  
  function removeItem(index) {
    if (workingItems.length <= minItems) return;
    
    const newItems = workingItems.filter((_, i) => i !== index);
    workingItems = newItems;
    dispatch('update', newItems);
  }
  
  function updateItem(index, value) {
    const newItems = [...workingItems];
    newItems[index] = value;
    workingItems = newItems;
    dispatch('update', newItems);
  }
  
  function moveItem(fromIndex, toIndex) {
    if (toIndex < 0 || toIndex >= workingItems.length) return;
    
    const newItems = [...workingItems];
    const [movedItem] = newItems.splice(fromIndex, 1);
    newItems.splice(toIndex, 0, movedItem);
    workingItems = newItems;
    dispatch('update', newItems);
  }
  
  // Ensure we have minimum items when component initializes
  $: if (workingItems.length < minItems && minItems > 0) {
    const newItems = [...workingItems];
    while (newItems.length < minItems) {
      newItems.push('');
    }
    if (newItems.length !== workingItems.length) {
      workingItems = newItems;
      dispatch('update', newItems);
    }
  }
</script>

<div class="array-editor">
  {#each workingItems as item, idx}
    <div class="array-item" class:empty={!item && !allowEmpty}>
      <div class="item-controls">
        {#if workingItems.length > 1}
          <button 
            class="btn btn-tiny btn-secondary"
            disabled={idx === 0}
            on:click={() => moveItem(idx, idx - 1)}
            title="Move up"
          >
            ↑
          </button>
          <button 
            class="btn btn-tiny btn-secondary"
            disabled={idx === workingItems.length - 1}
            on:click={() => moveItem(idx, idx + 1)}
            title="Move down"
          >
            ↓
          </button>
        {/if}
      </div>
      
      <div class="item-input">
        {#if itemType === 'textarea'}
          <textarea 
            value={item || ''}
            {placeholder}
            rows="2"
            on:input={(e) => updateItem(idx, e.target.value)}
          ></textarea>
        {:else if itemType === 'number'}
          <input 
            type="number"
            value={item || ''}
            {placeholder}
            on:input={(e) => updateItem(idx, e.target.value)}
          />
        {:else}
          <input 
            type="text"
            value={item || ''}
            {placeholder}
            on:input={(e) => updateItem(idx, e.target.value)}
          />
        {/if}
      </div>
      
      <div class="item-actions">
        {#if workingItems.length > minItems}
          <button 
            class="btn btn-small btn-danger"
            on:click={() => removeItem(idx)}
            title="Remove item"
          >
            ×
          </button>
        {/if}
      </div>
    </div>
  {/each}
  
  <div class="array-footer">
    <button class="btn btn-secondary" on:click={addItem}>
      Add Item
    </button>
    
    <div class="array-info">
      {workingItems.length} items
      {#if minItems > 0}
        (min: {minItems})
      {/if}
    </div>
  </div>
</div>

<style lang="scss">
  .array-editor {
    border: 2px solid #e5e7eb;
    border-radius: 6px;
    padding: 10px;
    background: rgba(255, 255, 255, 0.5);
  }
  
  .array-item {
    display: flex;
    gap: 8px;
    align-items: flex-start;
    margin-bottom: 8px;
    padding: 8px;
    border-radius: 4px;
    transition: all 0.2s ease;
    
    &:hover {
      background: rgba(59, 130, 246, 0.05);
    }
    
    &.empty {
      background: rgba(248, 113, 113, 0.1);
      border: 1px solid #fca5a5;
    }
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  .item-controls {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 0 0 auto;
  }
  
  .item-input {
    flex: 1;
    
    input, textarea {
      width: 100%;
      padding: 6px 10px;
      border: 1px solid #d1d5db;
      border-radius: 4px;
      background: #fff;
      font-size: 0.75rem;
      
      &:focus {
        outline: none;
        border-color: #3b82f6;
        box-shadow: 0 0 0 1px #3b82f6;
      }
    }
    
    textarea {
      resize: vertical;
      min-height: 60px;
    }
  }
  
  .item-actions {
    display: flex;
    align-items: center;
    flex: 0 0 auto;
  }
  
  .array-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid #e5e7eb;
  }
  
  .array-info {
    font-size: 0.7rem;
    color: #6b7280;
    font-style: italic;
  }
  
  .btn {
    padding: 4px 8px;
    border: none;
    border-radius: 3px;
    cursor: pointer;
    font-size: 11px;
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    transition: all 0.2s ease;
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    
    &.btn-tiny {
      padding: 2px 4px;
      font-size: 10px;
      line-height: 1;
    }
    
    &.btn-small {
      padding: 4px 8px;
      font-size: 12px;
    }
    
    &.btn-secondary {
      background: #10b981;
      color: white;
      
      &:hover:not(:disabled) {
        background: #059669;
      }
    }
    
    &.btn-danger {
      background: #ef4444;
      color: white;
      
      &:hover {
        background: #dc2626;
      }
    }
  }
</style>